import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Icon } from '@column/column-ui-kit';
import { Button, Dialog, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input } from '@column/ui';

import { NotificationList } from '~/components';
import { RenderFields } from '~/components/RenderFields';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { AccountNumber, BankAccountRepository } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';

interface AccountNumberModalProps {
  accountNumber?: AccountNumber;
  bankAccountId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: AccountNumber) => void;
}

interface AccountNumberForm {
  accountDescription: string;
}

export const AccountNumberModal = ({
  accountNumber,
  bankAccountId,
  isOpen,
  onClose,
  onSuccess,
}: AccountNumberModalProps) => {
  const { addDangerNotification, addSuccessNotification, clearNotifications } = useNotificationStore();
  const { handleSubmitErrors } = useFormErrorHandler({ defaultDisplayType: 'popup' });

  // Preserve edit mode state during transition to prevent flash
  const [isEditMode, setIsEditMode] = useState(Boolean(accountNumber));

  useEffect(() => {
    if (isOpen) {
      setIsEditMode(Boolean(accountNumber));
    }
  }, [isOpen, accountNumber]);

  const defaultValues = useMemo<AccountNumberForm>(
    () => ({
      accountDescription: accountNumber?.description ?? '',
    }),
    [accountNumber]
  );

  const formMethods = useForm<AccountNumberForm>({ defaultValues });

  useEffect(() => {
    if (isOpen) {
      formMethods.reset(defaultValues);
      clearNotifications('popup');
    }
  }, [clearNotifications, defaultValues, formMethods, isOpen]);

  const accountNumberMutation = useMutation({
    mutationFn: (data: AccountNumberForm) => {
      const payload = { description: data.accountDescription || undefined };

      if (accountNumber) {
        return BankAccountRepository.updateAccountNumber(accountNumber.id, payload);
      }

      return BankAccountRepository.createAccountNumber(bankAccountId, payload);
    },
    onSuccess: (data) => {
      addSuccessNotification({
        content: isEditMode ? 'Account number updated' : 'Account number created',
      });
      onSuccess(data);
      onClose();
    },
    onError: (error) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to save account number',
        display: 'popup',
      });
    },
  });

  const isPending = accountNumberMutation.isPending;
  const submitLabel = isEditMode ? (isPending ? 'Saving…' : 'Save Changes') : isPending ? 'Creating…' : 'Create';

  const onSubmit = formMethods.handleSubmit((data) => accountNumberMutation.mutate(data), handleSubmitErrors);

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && !isPending && onClose()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-3 sm:max-w-md"
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the field instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        <DialogHeader className="-mx-6 -mt-2 flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 pb-4 dark:border-white/[0.08]">
          <DialogTitle>{isEditMode ? 'Edit Account Number' : 'Create Account Number'}</DialogTitle>
          <DialogClose asChild>
            {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
            <button
              type="button"
              aria-label="Close"
              disabled={isPending}
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground disabled:pointer-events-none disabled:opacity-50 dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>

        {/* Inside the dialog so errors stack above its overlay. */}
        <NotificationList display="popup" spacing="bottom" className="!m-0" />

        <FormProvider {...formMethods}>
          <form autoComplete="off" onSubmit={onSubmit} className="-mx-6 -mt-6 [--page-margin:24px]">
            {RenderFields<AccountNumberForm>({
              sections: [
                {
                  fields: [
                    {
                      id: 'accountDescription',
                      label: 'Description',
                      oneColumn: true,
                      rules: { required: true },
                      children: ({ value, onChange, onBlur, ref }, { error }) => (
                        <Input
                          ref={ref}
                          placeholder="Description"
                          aria-invalid={!!error}
                          disabled={isPending}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                  ],
                },
              ],
            })}
          </form>
        </FormProvider>

        <DialogFooter className="sm:justify-between">
          <Button variant="secondary" size="sm" type="button" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button size="sm" type="button" onClick={onSubmit} disabled={isPending}>
            {!isEditMode && <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
